import { SButton, SText } from '@setvi/shared/components';
import { InfoIconV2 } from '@setvi/shared/components/sicons';
import { useDialog } from '@setvi/shared/providers';
import { Box, Divider } from '@material-ui/core';
import NotInterestedRoundedIcon from '@material-ui/icons/NotInterestedRounded';

import BaseDialog, {
  Tabs
} from 'pages/auto-rfq/pages/details/components/base-dialog';
import { Catalog, ManualEntry } from 'pages/auto-rfq/pages/details/components';

import { useRFQStore } from 'pages/auto-rfq/store';
import { RfqMatchOverride } from 'pages/auto-rfq/interfaces';
import { useUpdateItem } from 'pages/auto-rfq/pages/details/components/products/hook/useUpdateItem';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { AnalyticsModules, useAnalytics } from '@setvi/shared/hooks';
import {
  iconStyle,
  NoMatchButtonContainer,
  NoMatchContainer,
  NoMatchContent
} from '../../styled';
import { ProductCardProps } from '../..';

const NoMatch = ({
  id,
  desc,
  index,
  productName,
  matchOverride,

  handleProductChange,
  handleManualEntry
}: Partial<ProductCardProps>) => {
  const { id: rfqId } = useParams();
  const { captureAnalytics } = useAnalytics();
  const { openDialog, closeDialog } = useDialog();
  const {
    updateProcessedProduct,

    products
  } = useRFQStore();

  const { handleItemUpdate } = useUpdateItem({ rfqId });

  const handleNoMatch = async () => {
    updateProcessedProduct(id, {
      isMatched: true,
      matchOverride: null
    });

    const currProduct = products?.find(f => f?.id === id);
    const saved = await handleItemUpdate({
      ...currProduct,
      isMatched: true,
      matchOverride: null
    });

    if (saved) {
      enqueueSnackbar(`Item successfully reverted`, {
        variant: 'success'
      });

      const currMatched =
        currProduct?.matchedInternalProducts?.[currProduct?.internalIndex || 0];
      const targetIsPL =
        currMatched?.isPrivateLabel || currMatched?.privateLabel || false;

      if (!currMatched) return;

      captureAnalytics(
        {
          action: 'change',
          subAction: 'Marked as a match',
          rfqId,
          id: currMatched?._id,
          targetProductId: currMatched?.sku || '',
          targetIsPL
        },
        AnalyticsModules.AutoRFQItem
      );
    }
  };

  const isMarkedAsNonMatch =
    matchOverride === RfqMatchOverride.MarkedAsNonMatched;

  return (
    <NoMatchContainer>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
        <Box
          style={{
            border: '1px solid #EEF0F2',
            background: '#F8F9FA',
            padding: 8,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '50%'
          }}>
          {isMarkedAsNonMatch ? (
            <NotInterestedRoundedIcon htmlColor="#FA2020" />
          ) : (
            <InfoIconV2 fill="#FA2020" />
          )}
        </Box>

        {!isMarkedAsNonMatch && (
          <SText fontColor="#5D7083" weight="semibold" title="No Match" />
        )}
      </Box>

      <Divider
        orientation="vertical"
        style={{
          margin: isMarkedAsNonMatch ? '0px 8px' : '0px 32px',
          height: '50%'
        }}
      />

      <NoMatchContent>
        <SText
          title={
            isMarkedAsNonMatch
              ? 'This Item is Marked as No Match'
              : 'No Matches found for this item'
          }
          weight="semibold"
        />

        <NoMatchButtonContainer>
          {isMarkedAsNonMatch ? (
            <SButton
              color="default"
              variant="outlined"
              onClick={handleNoMatch}
              style={{ ...iconStyle, color: '#5D7083' }}>
              Enable
            </SButton>
          ) : (
            <>
              <SButton
                color="default"
                variant="outlined"
                onClick={() =>
                  openDialog(
                    <BaseDialog
                      title={`Browse Catalog for ${productName || desc || '-'} from RFQ`}
                      onClose={closeDialog}
                      elements={[
                        {
                          content: (
                            <Catalog
                              onClose={closeDialog}
                              handleSubmit={p => {
                                handleProductChange(p, index - 1);
                                closeDialog();
                              }}
                            />
                          ),
                          name: 'Catalog',
                          key: Tabs.CATALOG
                        }
                      ]}
                    />
                  )
                }
                style={{ ...iconStyle, color: '#5D7083' }}>
                Browse Catalog
              </SButton>
              <SButton
                color="default"
                variant="outlined"
                onClick={() =>
                  openDialog(
                    <BaseDialog
                      title="Add Manually Product"
                      onClose={closeDialog}
                      elements={[
                        {
                          content: (
                            <ManualEntry
                              onClose={closeDialog}
                              handleSubmit={props => {
                                handleManualEntry(props, index - 1);
                                closeDialog();
                              }}
                            />
                          ),
                          name: 'Manual Entry',
                          key: Tabs.MANUAL
                        }
                      ]}
                    />
                  )
                }
                style={{ ...iconStyle, color: '#5D7083' }}>
                Add Manually
              </SButton>
            </>
          )}
        </NoMatchButtonContainer>
      </NoMatchContent>
    </NoMatchContainer>
  );
};

export default NoMatch;
